import { fileOf, isOnBoard, rankOf, squareAt } from "./board";
import type { Board, Color, Square } from "./types";

const KNIGHT_OFFSETS: readonly [number, number][] = [
  [1, 2],
  [2, 1],
  [2, -1],
  [1, -2],
  [-1, -2],
  [-2, -1],
  [-2, 1],
  [-1, 2],
];

const KING_OFFSETS: readonly [number, number][] = [
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
  [0, -1],
  [1, -1],
];

const ORTHOGONAL_DIRECTIONS: readonly [number, number][] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

const DIAGONAL_DIRECTIONS: readonly [number, number][] = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

/** true si `square` est attaquee par une piece de couleur `byColor`. */
export function isSquareAttacked(board: Board, square: Square, byColor: Color): boolean {
  const file = fileOf(square);
  const rank = rankOf(square);

  const pawnRankDelta = byColor === "w" ? -1 : 1;
  for (const fileDelta of [-1, 1]) {
    const f = file + fileDelta;
    const r = rank + pawnRankDelta;
    if (!isOnBoard(f, r)) continue;
    const piece = board[squareAt(f, r)];
    if (piece?.type === "p" && piece.color === byColor) return true;
  }

  for (const [df, dr] of KNIGHT_OFFSETS) {
    const f = file + df;
    const r = rank + dr;
    if (!isOnBoard(f, r)) continue;
    const piece = board[squareAt(f, r)];
    if (piece?.type === "n" && piece.color === byColor) return true;
  }

  for (const [df, dr] of KING_OFFSETS) {
    const f = file + df;
    const r = rank + dr;
    if (!isOnBoard(f, r)) continue;
    const piece = board[squareAt(f, r)];
    if (piece?.type === "k" && piece.color === byColor) return true;
  }

  for (const [df, dr] of ORTHOGONAL_DIRECTIONS) {
    let f = file + df;
    let r = rank + dr;
    while (isOnBoard(f, r)) {
      const piece = board[squareAt(f, r)];
      if (piece) {
        if (piece.color === byColor && (piece.type === "r" || piece.type === "q")) return true;
        break;
      }
      f += df;
      r += dr;
    }
  }

  for (const [df, dr] of DIAGONAL_DIRECTIONS) {
    let f = file + df;
    let r = rank + dr;
    while (isOnBoard(f, r)) {
      const piece = board[squareAt(f, r)];
      if (piece) {
        if (piece.color === byColor && (piece.type === "b" || piece.type === "q")) return true;
        break;
      }
      f += df;
      r += dr;
    }
  }

  return false;
}

export { KNIGHT_OFFSETS, KING_OFFSETS, ORTHOGONAL_DIRECTIONS, DIAGONAL_DIRECTIONS };
